import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, Game, GameEvent, LogData } from '../services/api';

export function ScoreAdmin() {
  const navigate = useNavigate();
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedGameId, setExpandedGameId] = useState<string | null>(null);
  const [editingGameId, setEditingGameId] = useState<string | null>(null);
  const [editedScores, setEditedScores] = useState<{ [ballId: number]: number }>({});
  // Cache for game events to prevent race conditions
  const [gameEventsCache, setGameEventsCache] = useState<{ [gameId: string]: GameEvent[] }>({});

  // Load games on mount
  useEffect(() => {
    loadGames();
  }, []);

  const loadGames = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.listGames(50);
      // Sort games by createdAt descending (newest first)
      const sortedGames = response.games.sort((a, b) => b.createdAt - a.createdAt);
      setGames(sortedGames);

      // Load events for all games to prevent race conditions
      await loadEventsForGames(sortedGames.map(g => g.gameId));
    } catch (err) {
      setError('Failed to load games');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load events for multiple games and cache them
  const loadEventsForGames = async (gameIds: string[]) => {
    // Load events for each game in parallel
    const promises = gameIds.map(async (gameId) => {
      // Skip if already cached
      if (gameEventsCache[gameId]) {
        return;
      }

      try {
        const response = await apiClient.getGame(gameId);
        // Sort events by ballId ascending
        const sortedEvents = response.events.sort((a, b) => a.ballId - b.ballId);
        setGameEventsCache(prev => ({
          ...prev,
          [gameId]: sortedEvents,
        }));
      } catch (err) {
        console.error(`Failed to load events for game ${gameId}:`, err);
        // Set empty array to prevent retry
        setGameEventsCache(prev => ({
          ...prev,
          [gameId]: [],
        }));
      }
    });

    await Promise.all(promises);
  };

  const toggleExpand = async (gameId: string) => {
    if (expandedGameId === gameId) {
      setExpandedGameId(null);
      setEditingGameId(null);
      setEditedScores({});
    } else {
      setExpandedGameId(gameId);
      setEditingGameId(null);
      setEditedScores({});
    }
  };

  const startEditing = async (gameId: string) => {
    // Get game details to initialize edited scores
    try {
      const response = await apiClient.getGame(gameId);
      const initialScores: { [ballId: number]: number } = {};
      response.events.forEach(event => {
        initialScores[event.ballId] = event.holeValue;
      });
      setEditedScores(initialScores);
      setEditingGameId(gameId);
    } catch (err) {
      console.error('Failed to load game for editing:', err);
    }
  };

  const cancelEditing = () => {
    setEditingGameId(null);
    setEditedScores({});
  };

  const saveEdits = (gameId: string, updatedScores: { [ballId: number]: number }) => {
    // Calculate new total
    const newTotal = Object.values(updatedScores).reduce((sum, score) => sum + score, 0);

    // Update the game in the list
    setGames(games.map(g =>
      g.gameId === gameId
        ? { ...g, totalScore: newTotal }
        : g
    ));

    console.log(`[ScoreAdmin] Updated game ${gameId} with new scores:`, updatedScores);
    console.log(`[ScoreAdmin] New total score: ${newTotal}`);

    // Close editing mode but don't expand details
    setEditingGameId(null);
    setEditedScores({});
  };

  const deleteGame = async (gameId: string) => {
    if (!window.confirm('Are you sure you want to delete this game? This action cannot be undone.')) {
      return;
    }

    try {
      await apiClient.deleteGame(gameId);

      // Remove game from list
      setGames(games.filter(g => g.gameId !== gameId));

      // Remove from cache
      setGameEventsCache(prev => {
        const newCache = { ...prev };
        delete newCache[gameId];
        return newCache;
      });

      // Close expanded view if this game was expanded
      if (expandedGameId === gameId) {
        setExpandedGameId(null);
      }

      console.log(`[ScoreAdmin] Deleted game ${gameId}`);
    } catch (err) {
      console.error('Failed to delete game:', err);
    }
  };

  const updateBallScore = (ballId: number, newScore: number) => {
    setEditedScores(prev => ({
      ...prev,
      [ballId]: newScore,
    }));
  };

  const ballScoreValues = [10, 20, 30, 40, 50, 100];

  const getBallScores = (game: Game): number[] => {
    const scores: number[] = [];
    for (let i = 0; i < 9; i++) {
      if (editingGameId === game.gameId && editedScores[i] !== undefined) {
        scores.push(editedScores[i]);
      } else {
        scores.push(0); // Will be filled from events if expanded
      }
    }
    return scores;
  };

  const calculateTotal = (gameId: string): number => {
    if (editingGameId === gameId) {
      return Object.values(editedScores).reduce((sum, score) => sum + score, 0);
    }
    const game = games.find(g => g.gameId === gameId);
    return game?.totalScore || 0;
  };

  return (
    <div style={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}>
        <h1 style={{ margin: 0, fontSize: '24px' }}>Score Administration</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={loadGames} style={buttonStyle('#2196F3')}>
            Refresh Games
          </button>
          <button onClick={() => navigate('/')} style={buttonStyle('#4CAF50')}>
            ← Back to Game
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '12px',
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderRadius: '4px',
          marginBottom: '20px',
          fontWeight: 'bold',
        }}>
          {error}
        </div>
      )}

      {/* Games List */}
      <div>
        <h2 style={{ marginBottom: '15px' }}>Games (Newest First)</h2>
        {loading ? (
          <p>Loading games...</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {games.map(game => (
              <GameRow
                key={game.gameId}
                game={game}
                events={gameEventsCache[game.gameId] || []}
                isExpanded={expandedGameId === game.gameId}
                isEditing={editingGameId === game.gameId}
                editedScores={editedScores}
                onToggleExpand={toggleExpand}
                onStartEdit={startEditing}
                onCancelEdit={cancelEditing}
                onSaveEdit={saveEdits}
                onUpdateScore={updateBallScore}
                onDeleteGame={deleteGame}
                ballScoreValues={ballScoreValues}
              />
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

interface GameRowProps {
  game: Game;
  events: GameEvent[];  // Events passed from parent cache
  isExpanded: boolean;
  isEditing: boolean;
  editedScores: { [ballId: number]: number };
  onToggleExpand: (gameId: string) => void;
  onStartEdit: (gameId: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: (gameId: string, updatedScores: { [ballId: number]: number }) => void;
  onUpdateScore: (ballId: number, score: number) => void;
  onDeleteGame: (gameId: string) => void;
  ballScoreValues: number[];
}

function GameRow({
  game,
  events,
  isExpanded,
  isEditing,
  editedScores,
  onToggleExpand,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onUpdateScore,
  onDeleteGame,
  ballScoreValues,
}: GameRowProps) {
  const [gameDetails, setGameDetails] = useState<{ game: Game; events: GameEvent[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [editingBallId, setEditingBallId] = useState<number | null>(null);
  const [logs, setLogs] = useState<LogData | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);

  // Load game details when expanded (for full details view)
  useEffect(() => {
    if (isExpanded && !gameDetails) {
      loadDetails();
      loadLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  const loadDetails = async () => {
    setLoading(true);
    try {
      const response = await apiClient.getGame(game.gameId);
      // Sort events by ballId ascending
      const sortedEvents = response.events.sort((a, b) => a.ballId - b.ballId);
      setGameDetails({ ...response, events: sortedEvents });
    } catch (err) {
      console.error('Failed to load game details:', err);
      // Set empty game details to prevent infinite loading on error
      setGameDetails({
        game: game,
        events: []
      });
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const logData = await apiClient.getLogs(game.gameId, 'browser');
      setLogs(logData);
    } catch (err) {
      console.warn('Failed to load logs:', err);
      setLogs(null);
    } finally {
      setLogsLoading(false);
    }
  };

  // Get ball scores (either from edited state or from events prop)
  // Returns number for scored balls, undefined for unthrown balls
  const getBallScores = (): (number | undefined)[] => {
    const scores: (number | undefined)[] = [];
    for (let i = 0; i < 9; i++) {
      if (isEditing && editedScores[i] !== undefined) {
        scores.push(editedScores[i]);
      } else {
        // Use events from props (cached in parent)
        const event = events.find(e => e.ballId === i);
        scores.push(event?.holeValue);
      }
    }
    return scores;
  };

  const ballScores = getBallScores();
  const totalScore = isEditing
    ? Object.values(editedScores).reduce((sum, score) => sum + score, 0)
    : game.totalScore;

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      overflow: 'hidden',
    }}>
      {/* Horizontal Score Display - Similar to Main Page */}
      <div style={{
        display: 'flex',
        alignItems: 'stretch',
        padding: '15px',
        gap: '10px',
      }}>
        {/* Game Info */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minWidth: '180px',
          paddingRight: '15px',
          borderRight: '2px solid #ddd',
        }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
            {new Date(game.createdAt).toLocaleString()}
          </div>
          <div style={{ fontSize: '10px', color: '#999' }}>
            {game.status} | {game.ballCount} balls
          </div>
        </div>

        {/* Ball Scores - Horizontal like main page with inline editing */}
        {ballScores.map((score, index) => {
          const ballId = index;
          const isEditingThisBall = isEditing && editingBallId === ballId;
          const hasScore = score !== undefined;

          return (
            <div
              key={index}
              onClick={() => {
                if (isEditing && !isEditingThisBall) {
                  setEditingBallId(ballId);
                }
              }}
              style={{
                flex: '0 0 60px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10px 5px',
                backgroundColor: isEditingThisBall ? '#fff3e0' : (hasScore ? '#e3f2fd' : '#f5f5f5'),
                border: `2px solid ${isEditingThisBall ? '#FF9800' : (hasScore ? '#2196F3' : '#ccc')}`,
                borderRadius: '4px',
                cursor: isEditing ? 'pointer' : 'default',
              }}
            >
              <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>
                Ball {ballId + 1}
              </div>

              {isEditingThisBall ? (
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={editedScores[ballId] !== undefined ? editedScores[ballId] : (score !== undefined ? score : '')}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    onUpdateScore(ballId, value);
                  }}
                  onBlur={() => setEditingBallId(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setEditingBallId(null);
                    }
                  }}
                  autoFocus
                  style={{
                    width: '50px',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: '#FF9800',
                    textAlign: 'center',
                    border: 'none',
                    backgroundColor: 'transparent',
                    outline: 'none',
                  }}
                />
              ) : (
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: hasScore ? (isEditing ? '#FF9800' : '#2196F3') : '#ccc' }}>
                  {hasScore ? score : '-'}
                </div>
              )}
            </div>
          );
        })}

        {/* Total Score */}
        <div style={{
          flex: '0 0 80px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '10px',
          backgroundColor: '#4CAF50',
          borderRadius: '4px',
          marginLeft: '5px',
        }}>
          <div style={{ fontSize: '10px', color: 'white', marginBottom: '2px' }}>
            TOTAL
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'white' }}>
            {totalScore}
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '5px',
          marginLeft: '10px',
          paddingLeft: '10px',
          borderLeft: '2px solid #ddd',
          justifyContent: 'center',
        }}>
          {!isEditing ? (
            <>
              <button
                onClick={() => onStartEdit(game.gameId)}
                style={{ ...buttonStyle('#FF9800'), padding: '8px 16px', fontSize: '14px' }}
              >
                Edit
              </button>
              <button
                onClick={() => onToggleExpand(game.gameId)}
                style={{ ...buttonStyle('#2196F3'), padding: '6px 12px', fontSize: '12px' }}
              >
                {isExpanded ? 'Hide' : 'Details'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={async () => {
                  setEditingBallId(null);

                  // Update gameDetails with edited scores
                  // IMPORTANT: Preserve original camera detection data for ML training
                  if (gameDetails) {
                    try {
                      // Save edited events to backend
                      const updatePromises = gameDetails.events.map(async (event) => {
                        const originalScore = event.originalValue !== undefined ? event.originalValue : event.holeValue;
                        const newScore = editedScores[event.ballId] !== undefined ? editedScores[event.ballId] : event.holeValue;

                        // Only update if the score actually changed from the original
                        const scoreChanged = newScore !== originalScore;

                        if (scoreChanged) {
                          console.log(`[ScoreAdmin] Updating event ${event.eventId} from ${originalScore} to ${newScore}`);
                          await apiClient.updateEvent(game.gameId, event.eventId, {
                            holeValue: newScore,
                            notes: `Manually corrected from ${originalScore} to ${newScore}`,
                          });
                        }

                        return {
                          ...event,
                          // Preserve original camera detection in originalValue
                          originalValue: originalScore,
                          // Update current value with edited score
                          holeValue: newScore,
                          // Flag as edited ONLY if score actually changed
                          isEdited: scoreChanged || event.isEdited || false,
                        };
                      });

                      const updatedEvents = await Promise.all(updatePromises);
                      const newTotal = Object.values(editedScores).reduce((sum, score) => sum + score, 0);

                      setGameDetails({
                        ...gameDetails,
                        game: { ...gameDetails.game, totalScore: newTotal },
                        events: updatedEvents
                      });

                      console.log('[ScoreAdmin] All edits saved to backend successfully');
                    } catch (error) {
                      console.error('[ScoreAdmin] Failed to save edits to backend:', error);
                      alert('Failed to save changes to server. Please try again.');
                      return;
                    }
                  }

                  onSaveEdit(game.gameId, editedScores);
                }}
                style={{ ...buttonStyle('#4CAF50'), padding: '8px 16px', fontSize: '14px' }}
              >
                Save
              </button>
              <button
                onClick={() => {
                  setEditingBallId(null);
                  onCancelEdit();
                }}
                style={{ ...buttonStyle('#f44336'), padding: '6px 12px', fontSize: '12px' }}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Expanded Details Section - Only shows when Details button is clicked */}
      {isExpanded && !isEditing && (
        <div style={{
          padding: '20px',
          backgroundColor: '#f5f5f5',
          borderTop: '2px solid #ddd',
        }}>
          {loading ? (
            <p>Loading details...</p>
          ) : !gameDetails ? (
            <p style={{ color: '#f44336' }}>Failed to load game details. Please try again.</p>
          ) : (
            <div style={{ display: 'flex', gap: '20px' }}>
              {/* Game Information - 30% */}
              <div style={{ flex: '0 0 30%' }}>
                <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '4px', height: '100%' }}>
                  <h3 style={{ marginBottom: '12px', fontSize: '16px' }}>Game Information</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '12px' }}>
                    <div>
                      <strong>Game ID:</strong>
                      <div style={{ color: '#666', marginTop: '2px', wordBreak: 'break-all' }}>
                        {gameDetails.game.gameId}
                      </div>
                    </div>
                    <div>
                      <strong>Status:</strong>
                      <div style={{ color: '#666', marginTop: '2px' }}>
                        {gameDetails.game.status}
                      </div>
                    </div>
                    <div>
                      <strong>Total Score:</strong>
                      <div style={{ color: '#2196F3', marginTop: '2px', fontSize: '20px', fontWeight: 'bold' }}>
                        {gameDetails.game.totalScore}
                      </div>
                    </div>
                    <div>
                      <strong>Ball Count:</strong>
                      <div style={{ color: '#666', marginTop: '2px' }}>
                        {gameDetails.game.ballCount}
                      </div>
                    </div>
                    <div>
                      <strong>Created:</strong>
                      <div style={{ color: '#666', marginTop: '2px' }}>
                        {new Date(gameDetails.game.createdAt).toLocaleString()}
                      </div>
                    </div>
                    {gameDetails.game.updatedAt && (
                      <div>
                        <strong>Updated:</strong>
                        <div style={{ color: '#666', marginTop: '2px' }}>
                          {new Date(gameDetails.game.updatedAt).toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Delete Button */}
                  <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
                    <button
                      onClick={() => onDeleteGame(game.gameId)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#d32f2f'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f44336'}
                    >
                      🗑️ Delete Game
                    </button>
                  </div>
                </div>
              </div>

              {/* Event Details - 70% */}
              <div style={{ flex: '0 0 70%' }}>
                <div style={{ backgroundColor: 'white', borderRadius: '4px', overflow: 'hidden' }}>
                  <h3 style={{ padding: '15px', margin: 0, fontSize: '16px', backgroundColor: '#e3f2fd' }}>
                    Event Details
                  </h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                          <th style={tableHeaderStyle}>Ball</th>
                          <th style={tableHeaderStyle}>Original Score</th>
                          <th style={tableHeaderStyle}>Current Score</th>
                          <th style={tableHeaderStyle}>Time</th>
                          <th style={tableHeaderStyle}>Position</th>
                          <th style={tableHeaderStyle}>Confidence</th>
                          <th style={tableHeaderStyle}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gameDetails.events.map(event => {
                          const originalScore = event.originalValue !== undefined ? event.originalValue : event.holeValue;
                          const currentScore = event.holeValue;
                          // Check both isManualEdit (from backend) and isEdited (local state)
                          const isEdited = event.isManualEdit || event.isEdited || false;

                          return (
                            <tr
                              key={event.eventId}
                              style={{
                                borderBottom: '1px solid #ddd',
                                backgroundColor: isEdited ? '#fff3e0' : 'transparent'
                              }}
                            >
                              <td style={tableCellStyle}>{event.ballId}</td>
                              <td style={{ ...tableCellStyle, fontWeight: 'bold', color: '#666' }}>
                                {originalScore}
                              </td>
                              <td style={{
                                ...tableCellStyle,
                                fontWeight: 'bold',
                                color: isEdited ? '#FF9800' : '#2196F3'
                              }}>
                                {currentScore}
                              </td>
                              <td style={tableCellStyle}>{new Date(event.timestamp).toLocaleTimeString()}</td>
                              <td style={tableCellStyle}>({Math.round(event.position.x)}, {Math.round(event.position.y)})</td>
                              <td style={tableCellStyle}>{(event.confidence * 100).toFixed(0)}%</td>
                              <td style={{ ...tableCellStyle, fontSize: '11px', color: '#FF9800', fontWeight: 'bold' }}>
                                {isEdited ? '✏️ Edited' : ''}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Browser Logs Section */}
                  <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>
                      Browser Console Logs
                    </h4>
                    {logsLoading ? (
                      <p style={{ fontSize: '12px', color: '#666' }}>Loading logs...</p>
                    ) : logs && logs.logs && Array.isArray(logs.logs) && logs.logs.length > 0 ? (
                      <div style={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        backgroundColor: '#1e1e1e',
                        padding: '10px',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '11px',
                      }}>
                        {logs.logs.map((log, index) => (
                          <div
                            key={index}
                            style={{
                              padding: '2px 0',
                              color: log.level === 'error' ? '#ff6b6b' :
                                     log.level === 'warn' ? '#ffd93d' :
                                     log.level === 'info' ? '#6bcf7f' :
                                     '#d4d4d4',
                              borderBottom: index < logs.logs.length - 1 ? '1px solid #333' : 'none',
                            }}
                          >
                            <span style={{ color: '#888' }}>
                              [{new Date(log.timestamp).toLocaleTimeString()}]
                            </span>
                            {' '}
                            <span style={{ color: '#6bcf7f', fontWeight: 'bold' }}>
                              [{log.level.toUpperCase()}]
                            </span>
                            {' '}
                            <span>{log.message}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                        No browser logs available for this game
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const tableHeaderStyle: React.CSSProperties = {
  padding: '8px',
  textAlign: 'left',
  fontWeight: 'bold',
  fontSize: '12px',
};

const tableCellStyle: React.CSSProperties = {
  padding: '8px',
  fontSize: '12px',
};

function buttonStyle(bgColor: string): React.CSSProperties {
  return {
    padding: '8px 16px',
    backgroundColor: bgColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
  };
}

